import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { getEmptyBanner } from '../../interfaces/Banner/Banner';
import {
  createBanner,
  deleteBanner,
  getBanner,
  updateBanner,
  updateBannerMedia,
} from '../../api/Banner';
import { BannerMediaOptions } from '../../interfaces/Banner/BannerMediaOptions';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import { ImageInput } from '../../components/ImageInput/ImageInput';
import NumberInput from '../../components/NumberInput/NumberInput';
import { LanguageDropdown } from '../../components/Dropdowns/LanguageDropdown';

export const ModifyBannerPage = () => {
  const navigate = useNavigate();

  const { bannerID: bannerIDAsStr } = useParams<string>();

  const bannerID = bannerIDAsStr ? Number(bannerIDAsStr) : undefined;
  const isEditing = Number.isFinite(bannerID);

  const [banner, setBanner] = useState(getEmptyBanner());
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshBanner = () => {
    setLoading(true);
    getBanner(bannerID!)
      .then((b) => setBanner(b))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshBanner();
    }
  }, [bannerIDAsStr]);

  const handleSave = () => {
    if (isEditing) {
      setLoading(true);
      updateBanner(banner)
        .then((updatedBanner) => {
          toast.success('Banner başarıyla güncellendi');
          setBanner(updatedBanner);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createBanner(banner)
        .then((createdBanner) => {
          toast.success('Banner başarıyla oluşturuldu');
          navigate(`/visual/banners/${createdBanner.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleUpdateMedia = (options: BannerMediaOptions) => {
    setLoading(true);
    updateBannerMedia(banner.id, options)
      .then((updatedBanner) => {
        toast.success('Medya başarıyla güncellendi');
        setBanner(updatedBanner);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteBanner(banner.id)
      .then(() => {
        toast.success('Banner başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/visual/banners');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili bannerı silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Bannerı Düzenle' : 'Yeni Banner'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 400,
          height: 'fit-content',
          p: 3,
          gap: 1.5,
        }}
      >
        <TextField
          label='Başlık'
          variant='outlined'
          size='small'
          value={banner.title}
          onChange={(e) => setBanner({ ...banner, title: e.target.value })}
          fullWidth
        />
        <TextField
          label='Link'
          variant='outlined'
          size='small'
          value={banner.linkUrl}
          onChange={(e) => setBanner({ ...banner, linkUrl: e.target.value })}
          fullWidth
        />
        <NumberInput
          label='Sıra'
          value={banner.sortOrder}
          onChange={(newOrder) => setBanner({ ...banner, sortOrder: newOrder })}
        />
        <LanguageDropdown
          label='Hedef Dil'
          allowDefault
          defaultOptionLabel='Tüm Diller'
          value={banner.locale || null}
          onChange={(x) => setBanner({ ...banner, locale: x || undefined })}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={banner.isActive}
                onChange={() =>
                  setBanner({ ...banner, isActive: !banner.isActive })
                }
              />
            }
            label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kaydet
          </Button>
        </Box>
      </Paper>
      {isEditing && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            mt: 1.5,
            gap: 1.5,
          }}
        >
          <ImageInput
            title='Resim'
            existingImage={banner.image}
            onUpload={(file) => handleUpdateMedia({ image: file })}
            onDelete={() => handleUpdateMedia({ eraseImage: true })}
          />
        </Box>
      )}
    </Box>
  );
};
